import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Icon } from "@iconify/react";

const ProductDetails = () => {
  const { id } = useParams();
  const [product, setProduct] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cartQty, setCartQty] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    fetch(`https://servercareseebd.careseebd.com/api/v1/products/${id}`, { signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch product details");
        }
        return response.json();
      })
      .then((data) => {
        setProduct(data || {});
        setLoading(false);

        // Fetch current quantity in cart
        const cart = JSON.parse(localStorage.getItem("cart")) || [];
        const match = cart.find((item) => item._id === id);
        if (match) {
          setCartQty(match.quantity);
        }
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setError(error.message);
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [id]);

  const updateCartQuantity = (newQty) => {
    let cart = JSON.parse(localStorage.getItem("cart")) || [];
    const index = cart.findIndex((item) => item._id === product._id);

    if (newQty <= 0) {
      cart = cart.filter((item) => item._id !== product._id);
      setCartQty(0);
    } else {
      if (index !== -1) {
        cart[index].quantity = newQty;
      } else {
        cart.push({ ...product, quantity: newQty });
      }
      setCartQty(newQty);
    }
    localStorage.setItem("cart", JSON.stringify(cart));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm font-semibold text-gray-400">Loading details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto text-center py-16 bg-white border border-gray-100 rounded-2xl shadow-sm mt-12">
        <Icon icon="solar:shield-warning-broken" className="w-16 h-16 text-rose-500 mx-auto mb-4" />
        <p className="text-base text-gray-500 font-semibold mb-4">{error}</p>
        <Link
          to="/accessories"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider"
        >
          Back to Shop
        </Link>
      </div>
    );
  }

  const hasStock = product.stock > 0;

  return (
    <div className="bg-slate-50 min-h-screen pb-24 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(99,102,241,0.04),transparent)] pointer-events-none" />

      {/* Top Breadcrumb/Back link */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 relative z-10">
        <Link
          to="/accessories"
          className="inline-flex items-center gap-2 text-xs font-bold text-gray-500 hover:text-indigo-600 transition-colors uppercase tracking-wider mb-8"
        >
          <Icon icon="solar:arrow-left-linear" className="w-4 h-4" />
          Back to Shop
        </Link>

        {/* Product Details Split */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 bg-white border border-gray-100 rounded-3xl p-6 sm:p-10 shadow-sm relative z-10">

          {/* Left: Product Image Box */}
          <div className="relative rounded-2xl bg-gray-50 overflow-hidden border border-gray-100 flex items-center justify-center min-h-[300px] sm:min-h-[450px]">
            <img
              src={product.imageUrl}
              alt={product.name || "Product Image"}
              className="w-full h-full object-cover max-h-[500px]"
            />
            {/* Stock Level Badge */}
            <span className={`absolute top-4 left-4 z-10 px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-wider shadow-sm ${hasStock
              ? "bg-emerald-500 text-white"
              : "bg-rose-500 text-white"
              }`}>
              {hasStock ? `Available (${product.stock})` : "Out of Stock"}
            </span>
          </div>

          {/* Right: Info and Specs */}
          <div className="flex flex-col justify-between">
            <div>
              {/* Title & Tag */}
              <div className="flex flex-wrap items-center gap-2.5 mb-4">
                <span className="bg-indigo-50 text-indigo-600 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-indigo-100">
                  {product.categories || "Accessory"}
                </span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight mb-4">
                {product.name}
              </h1>

              {/* Price Row */}
              <div className="flex items-baseline gap-1.5 mb-6">
                <span className="text-3xl font-extrabold font-heading text-indigo-600">
                  ৳{product.price}
                </span>
                <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider">
                  VAT Included
                </span>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2.5">
                  Description
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                  {product.description?.replace(/<\/?[^>]+(>|$)/g, "") || "No description provided for this item."}
                </p>
              </div>

              {/* Product Specifications Grid */}
              <div className="grid grid-cols-2 gap-4 bg-slate-50 border border-slate-100 rounded-2xl p-5 mb-8">
                <div>
                  <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    Weight
                  </span>
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
                    <Icon icon="solar:scale-bold" className="w-4 h-4 text-indigo-500" />
                    {product.weight ? `${product.weight} g` : "N/A"}
                  </div>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    Dimensions
                  </span>
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
                    <Icon icon="solar:ruler-bold" className="w-4 h-4 text-indigo-500" />
                    {product.dimensions ? `${product.dimensions} cm` : "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {/* Stepper / Action row */}
            <div className="flex flex-col sm:flex-row items-center gap-4 pt-6 border-t border-gray-100">
              <div className="flex items-center justify-between border border-gray-200 rounded-xl px-2.5 py-1.5 bg-white w-full sm:w-auto">
                <button
                  onClick={() => updateCartQuantity(cartQty - 1)}
                  className="w-8 h-8 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-500 flex items-center justify-center transition-colors"
                  disabled={cartQty <= 0}
                >
                  <Icon icon="solar:minus-circle-bold" className="w-5 h-5" />
                </button>
                <span className="w-12 text-center text-sm font-bold text-gray-800">
                  {cartQty}
                </span>
                <button
                  onClick={() => updateCartQuantity(cartQty + 1)}
                  className="w-8 h-8 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-500 flex items-center justify-center transition-colors"
                  disabled={!hasStock}
                >
                  <Icon icon="solar:add-circle-bold" className="w-5 h-5" />
                </button>
              </div>

              {cartQty === 0 ? (
                <button
                  onClick={() => updateCartQuantity(1)}
                  disabled={!hasStock}
                  className="w-full sm:flex-1 py-3 px-6 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-100 disabled:text-gray-400 text-white text-xs font-bold rounded-xl transition-all uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Icon icon="solar:cart-plus-bold" className="w-4 h-4" />
                  Add to Cart
                </button>
              ) : (
                <Link
                  to="/accessories/cart"
                  className="w-full sm:flex-1 py-3 px-6 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-all uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer text-center"
                >
                  <Icon icon="solar:cart-large-4-bold" className="w-4 h-4" />
                  View in Cart
                </Link>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
